import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Radio from "@mui/material/Radio";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormControlLabel from "@mui/material/FormControlLabel";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __adjustBalancePWG } from "../../../../api/sim";
import { ADJUST_BALANCE_UNITS } from "../../../../utils/adjustBalance";

const BALANCE_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "voice", label: "Voice (minutes)" },
  { value: "data", label: "Data (MB)" },
];

const AdjustBalanceForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [linesError, setLinesError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const { setAlertMessage } = useAlert();
  const initialData = { balance_type: "text", amount: "", lines: [] };
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__adjustBalancePWG,
  });

  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    esn: yup
      .string("esn")
      .min(19, "Should be at least 19 digits long")
      .max(25, "Should be at most 25 digits long")
      .required(),
  });

  useEffect(() => {
    if (formData) {
      call({
        data: {
          balance_type: formik.values.balance_type,
          amount: Number(formik.values.amount),
          lines: linesData,
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({
        type: "success",
        message: "Adjust balance queue started",
      });
      setLinesData([]);
      nav("/pwg/adjust_balance");
    }
  }, [response]);

  // Validates the ESN lines. Kept out of formik's errors because formik
  // re-validates on every change and would wipe errors set via setErrors.
  const validateFunction = (data) => {
    const formatedLines = [];
    let error = "";

    const lines = (data || "").split("\n").filter((line) => line.trim());
    lines.some((line, idx) => {
      try {
        const obtainedData = { esn: line.trim() };

        lineValidationSchema.validateSync(obtainedData);
        formatedLines.push(obtainedData);
      } catch (err) {
        error = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    setLinesError(error);
    setLinesData(error ? [] : formatedLines);
    return error;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema: yup.object({
      balance_type: yup.string().oneOf(["text", "voice", "data"]).required(),
      amount: yup
        .number()
        .typeError("Amount must be a number")
        .integer("Amount must be an integer")
        .notOneOf([0], "Amount cannot be 0")
        .required("Amount is required"),
    }),
    onSubmit: () => {
      setFormData({ submitted: true });
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      validateFunction(target.result);
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        setUploadError("Please input a csv file");
        return;
      }

      setUploadError("");

      handleParse(inputFile);
    }
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content="Adjust Balance (PWG)"
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="balance_type" sx={{ position: "static" }}>
                Balance Type
              </InputLabel>
              <RadioGroup
                row
                name="balance_type"
                value={formik.values.balance_type}
                onChange={(e) =>
                  formik.setFieldValue("balance_type", e.target.value)
                }
              >
                {BALANCE_TYPE_OPTIONS.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <TextField
              fullWidth
              type="number"
              name="amount"
              label={`Amount (${ADJUST_BALANCE_UNITS[formik.values.balance_type]})`}
              helperText={formik.touched.amount && formik.errors.amount}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(uploadError)}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(uploadError) ? (
                <FormHelperText id="upload-error-text">
                  {uploadError}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {"Add in the format"}
              <br></br>
              {"ESN"}
              <br></br>
              {"ESN"}
              <br></br>
            </InputLabel>
            <FormControl
              error={Boolean(linesError)}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\nESN\nESN`}
                style={{ minWidth: 550 }}
                minRows={5}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  validateFunction(data);
                }}
              />
              {Boolean(linesError) ? (
                <FormHelperText id="lines-error-text">
                  {linesError}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  Boolean(linesError) ||
                  Boolean(formik.errors.amount) ||
                  !linesData.length ||
                  !formik.values.amount
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Adjust Balance"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AdjustBalanceForm;
